import React, { useEffect, useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import Input from '../../shared/Input';
import CustomSelect from '../../shared/CustomSelect';
import ModalShell from '../../shared/ModalShell';
import { AREA_TYPE_OPTIONS, locationToForm } from '../../../services/vendorLocationService';
import useOperatingMarkets from '../../../hooks/useOperatingMarkets';

const LocationFormModal = ({ isOpen, onClose, onSubmit, initialLocation, isSaving, primaryLocation }) => {
 const [form, setForm] = useState(locationToForm());
 const [errors, setErrors] = useState({});
 const { options: marketOptions, isLoading: marketsLoading, isEmpty } = useOperatingMarkets({
 activeOnly: true,
 });

 useEffect(() => {
 if (isOpen) {
 setForm(locationToForm(initialLocation));
 setErrors({});
 }
 }, [isOpen, initialLocation]);

 const handleClose = () => {
 if (!isSaving) onClose();
 };

 const setField = (key, value) => {
 setForm((prev) => ({ ...prev, [key]: value }));
 if (errors[key]) setErrors((prev) => ({ ...prev, [key]: null }));
 };

 const validate = () => {
 const next = {};
 if (!form.area_name?.trim()) next.area_name = 'Location name is required';
 if (!form.area_type) next.area_type = 'Area type is required';
 if (form.country && !marketOptions.some((option) => option.value === String(form.country).toUpperCase())) {
 next.country = 'Select an operating market';
 }
 if (form.travel_fee !== '' && Number(form.travel_fee) < 0) next.travel_fee = 'Must be 0 or greater';
 if (form.travel_time_minutes !== '' && Number(form.travel_time_minutes) < 0) {
 next.travel_time_minutes = 'Must be 0 or greater';
 }
 setErrors(next);
 return Object.keys(next).length === 0;
 };

 const handleSubmit = (e) => {
 e.preventDefault();
 if (!validate()) return;

 const payload = {
 area_name: form.area_name.trim(),
 area_type: form.area_type,
 city: form.city?.trim() || null,
 state: form.state?.trim() || null,
 country: form.country?.trim() || null,
 address_line: form.address_line?.trim() || null,
 notes: form.notes?.trim() || null,
 is_active: form.is_active,
 travel_fee: form.travel_fee === '' ? 0 : Number(form.travel_fee),
 travel_time_minutes: form.travel_time_minutes === '' ? 0 : parseInt(form.travel_time_minutes, 10),
 };

 if (form.latitude !== '' && form.longitude !== '') {
 payload.latitude = Number(form.latitude);
 payload.longitude = Number(form.longitude);
 }

 onSubmit(payload);
 };

 const prefillFromPrimary = () => {
 if (!primaryLocation) return;
 setForm((prev) => ({
 ...prev,
 city: primaryLocation.city || prev.city,
 state: primaryLocation.state || prev.state,
 country: primaryLocation.country || prev.country,
 address_line: primaryLocation.address || prev.address_line,
 latitude: primaryLocation.latitude ?? prev.latitude,
 longitude: primaryLocation.longitude ?? prev.longitude,
 }));
 };

 return (
 <ModalShell
 isOpen={isOpen}
 onClose={handleClose}
 labelledBy="location-form-title"
 panelClassName="max-w-lg"
 >
 <div className="bg-surface rounded-lg shadow-sm border border-border overflow-hidden flex flex-col max-h-[min(90vh,900px)]">
 <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-border shrink-0">
 <div>
 <h2 id="location-form-title" className="text-lg font-semibold text-ink">
 {initialLocation ? 'Edit service location' : 'Add service location'}
 </h2>
 <p className="text-xs text-ink-tertiary mt-0.5">
 Active areas appear on your public profile under Service areas.
 </p>
 </div>
 <button
 type="button"
 onClick={handleClose}
 disabled={isSaving}
 className="p-2 rounded-md hover:bg-surface-muted disabled:opacity-50"
 aria-label="Close"
 >
 <X className="w-5 h-5 text-ink-tertiary" />
 </button>
 </div>

 <form onSubmit={handleSubmit} className="overflow-y-auto flex-1 px-4 sm:px-6 py-4 space-y-4">
 {!initialLocation && primaryLocation?.city && (
 <button
 type="button"
 onClick={prefillFromPrimary}
 className="text-xs text-ink hover:underline"
 >
 Prefill city & address from your primary business location
 </button>
 )}

 <Input
 label="Location name *"
 value={form.area_name}
 onChange={(e) => setField('area_name', e.target.value)}
 error={errors.area_name}
 placeholder="e.g. East Legon, Downtown Houston"
 />

 <CustomSelect
 label="Area type *"
 value={form.area_type}
 onChange={(val) => setField('area_type', val)}
 options={AREA_TYPE_OPTIONS}
 error={errors.area_type}
 />

 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
 <Input
 label="City"
 value={form.city}
 onChange={(e) => setField('city', e.target.value)}
 placeholder="Accra"
 />
 <Input
 label="State / region"
 value={form.state}
 onChange={(e) => setField('state', e.target.value)}
 placeholder="Greater Accra"
 />
 </div>

 <CustomSelect
 label="Market"
 value={form.country || ''}
 onChange={(value) => setField('country', value)}
 options={marketOptions}
 placeholder={marketsLoading ? 'Loading markets…' : 'Select market'}
 error={errors.country || (isEmpty ? 'No markets are set up yet' : '')}
 disabled={marketsLoading || isEmpty}
 />

 <Input
 label="Street address (optional)"
 value={form.address_line}
 onChange={(e) => setField('address_line', e.target.value)}
 placeholder="123 Main Street"
 />

 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
 <Input
 label="Travel fee ($)"
 type="number"
 min="0"
 step="0.01"
 value={form.travel_fee}
 onChange={(e) => setField('travel_fee', e.target.value)}
 error={errors.travel_fee}
 placeholder="0.00"
 />
 <Input
 label="Est. travel time (minutes)"
 type="number"
 min="0"
 value={form.travel_time_minutes}
 onChange={(e) => setField('travel_time_minutes', e.target.value)}
 error={errors.travel_time_minutes}
 placeholder="30"
 />
 </div>

 <div>
 <label htmlFor="location-notes" className="block text-sm font-medium text-ink-secondary mb-1">
 Internal notes (optional)
 </label>
 <textarea
 id="location-notes"
 rows={3}
 value={form.notes}
 onChange={(e) => setField('notes', e.target.value)}
 className="w-full px-3 py-2 border border-border bg-surface text-ink rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-ink/40"
 placeholder="Parking instructions, setup access, etc."
 />
 </div>

 <label className="flex items-center gap-3 cursor-pointer">
 <input
 type="checkbox"
 checked={form.is_active}
 onChange={(e) => setField('is_active', e.target.checked)}
 className="rounded border-gray-300 text-ink focus:ring-ink"
 />
 <span className="text-sm text-ink-secondary">Active — visible on your public profile</span>
 </label>
 </form>

 <div className="flex gap-3 px-4 sm:px-6 py-4 border-t border-border bg-surface-muted shrink-0">
 <button
 type="button"
 onClick={handleClose}
 disabled={isSaving}
 className="flex-1 px-4 py-1.5 text-xs font-medium border border-border text-gray-800 rounded-md bg-surface hover:bg-surface-muted disabled:opacity-50"
 >
 Cancel
 </button>
 <button
 type="button"
 onClick={handleSubmit}
 disabled={isSaving}
 className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-1.5 text-xs font-medium rounded-md bg-ink text-white hover:bg-black disabled:opacity-50"
 >
 {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
 {initialLocation ? 'Save changes' : 'Add location'}
 </button>
 </div>
 </div>
 </ModalShell>
 );
};

export default LocationFormModal;
